import React, {
  useMemo,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { HbrBarChart, HbrSpinner } from '@harbor/elements-react-standalone';
import { buildHarborItemTooltip } from '../../../support/harborTooltip';
import { applyCommonHarborRuntimeInModifyConfig } from '../../../support/harborChartRuntimeUtils';
import { normalizeNivoBorderColor } from '../../../support/chartStyleValueUtils';
import {
  getContextualChartMargins,
  measureChartTextWidth,
} from './chartLayoutUtils';
import { makeSmartValueFormatter } from './tileUtils';

interface ChartValue {
  key: string;
  value: number;
}
interface ChartDatum {
  label?: string;
  values?: ChartValue[];
  tooltip?: { [k: string]: string | number };
  [key: string]: any;
}
interface ChartData {
  keys?: string[];
  data: ChartDatum[];
}

interface BarChartTileProps {
  data: ChartData | any;
  tileDef: any;
  layoutHeight: number;
  layoutWidth: number;
  direction?: 'vertical' | 'horizontal';
  showLegendOverride?: boolean;
  setError?: (msg: string | null) => void;
  initialized?: boolean;
  displayContext?: 'preview' | 'dashboard' | 'leader';
}

const DEFAULT_GROUP_MODE: 'stacked' | 'grouped' = 'stacked';
const DEFAULT_AXIS_TICK_SIZE = 5;
const DEFAULT_AXIS_TICK_PADDING = 5;
const DEFAULT_BOTTOM_LEGEND_OFFSET = 40;
const DEFAULT_LEFT_LEGEND_OFFSET = -55;
const DEFAULT_MAX_LEFT_MARGIN_RATIO = 0.45;

const LABEL_FONT_FAMILY = 'Inter, Arial, sans-serif';
const LABEL_FONT_SIZE_PX = 12;

const stableStringify = (value: any): string => {
  const sortKeys = (obj: any): any => {
    if (Array.isArray(obj)) return obj.map(sortKeys);
    if (obj && typeof obj === 'object') {
      return Object.keys(obj)
        .sort()
        .reduce((acc: any, key) => {
          acc[key] = sortKeys(obj[key]);
          return acc;
        }, {});
    }
    return obj;
  };

  try {
    return JSON.stringify(sortKeys(value));
  } catch {
    return '';
  }
};

function sumRow(keys: string[], row: any): number {
  let total = 0;
  for (const k of keys) {
    const v = Number(row?.[k]);
    if (Number.isFinite(v)) total += v;
  }
  return total;
}

function buildBarChartData(input: any, tileDef: any): { formattedData: any[]; keys: string[] } {
  const s = tileDef?.settings || {};
  const chartDataLike = input && typeof input === 'object' && Array.isArray(input.data);

  if (chartDataLike) {
    const items = Array.isArray(input.data) ? input.data : [];
    const providedKeys = Array.isArray(input.keys) ? input.keys.filter(Boolean).map(String) : [];

    const inferredKeys = (() => {
      const first = items?.[0];
      if (!first) return [];
      if (Array.isArray((first as any).values)) {
        return (first as any).values.map((v: any) => String(v.key));
      }
      const exclude = new Set(['label', '__tooltip']);
      return Object.keys(first).filter(
        (k) => !exclude.has(k) && typeof (first as any)[k] === 'number'
      );
    })();

    const effectiveKeys = providedKeys.length ? providedKeys : inferredKeys;

    const formattedData = items.map((item: any, idx: number) => {
      const label =
        item.label ??
        (s?.labelField && item?.[s.labelField] != null ? String(item[s.labelField]) : undefined) ??
        String(idx + 1);

      const entry: any = { label };

      if (Array.isArray(item.values)) {
        for (const { key, value } of item.values) entry[String(key)] = Number(value) || 0;
      } else {
        for (const k of effectiveKeys) entry[k] = Number(item?.[k]) || 0;
      }

      if (item.tooltip) entry.__tooltip = item.tooltip;
      return entry;
    });

    return { formattedData, keys: effectiveKeys };
  }

  const rows: any[] = Array.isArray(input) ? input : Array.isArray(input?.data) ? input.data : [];
  const labelField = s.labelField;
  const seriesField = s.seriesField;
  const valueField = s.valueField;

  if (!rows.length || !labelField) return { formattedData: [], keys: [] };

  const aggregation = (s.aggregate ?? 'auto') as 'auto' | 'count' | 'sum' | 'avg' | 'min' | 'max';
  const reducer =
    aggregation === 'auto'
      ? valueField === 'Count'
        ? 'count'
        : valueField === 'Sum'
          ? 'sum'
          : 'sum'
      : aggregation;

  const labelKey = (r: any) => String(r?.[labelField] ?? '');
  const seriesKey = (r: any) =>
    String(seriesField ? r?.[seriesField] ?? 'Series' : s.defaultSeriesName ?? 'Value');

  const rowValue = (r: any) => {
    if (reducer === 'count') return 1;
    if (!valueField || valueField === 'Count' || valueField === 'Sum') {
      const vc = s.valueColumn;
      return Number(vc ? r?.[vc] : r?.value) || 0;
    }
    return Number(r?.[valueField]) || 0;
  };

  type AggState = { sum: number; count: number; min: number; max: number };
  const table = new Map<string, Map<string, AggState>>();
  const keysSet = new Set<string>();

  for (const r of rows) {
    const L = labelKey(r);
    if (!L) continue;

    const S = seriesKey(r);
    keysSet.add(S);

    const val = rowValue(r);

    let inner = table.get(L);
    if (!inner) {
      inner = new Map<string, AggState>();
      table.set(L, inner);
    }

    let st = inner.get(S);
    if (!st) {
      st = {
        sum: 0,
        count: 0,
        min: Number.POSITIVE_INFINITY,
        max: Number.NEGATIVE_INFINITY,
      };
      inner.set(S, st);
    }

    st.sum += val;
    st.count += 1;
    st.min = Math.min(st.min, val);
    st.max = Math.max(st.max, val);
  }

  const keys = Array.from(keysSet);
  const keysOverride = Array.isArray(s.keys) ? s.keys.map(String) : null;
  const effectiveKeys = keysOverride?.length ? keysOverride : keys;

  const compute = (st: AggState) => {
    switch (reducer) {
      case 'count':
        return st.count;
      case 'avg':
        return st.count ? st.sum / st.count : 0;
      case 'min':
        return st.min === Number.POSITIVE_INFINITY ? 0 : st.min;
      case 'max':
        return st.max === Number.NEGATIVE_INFINITY ? 0 : st.max;
      case 'sum':
      default:
        return st.sum;
    }
  };

  const formattedData = Array.from(table.entries()).map(([label, seriesMap]) => {
    const entry: any = { label };
    for (const k of effectiveKeys) {
      const st = seriesMap.get(k);
      entry[k] = st ? compute(st) : 0;
    }
    return entry;
  });

  return { formattedData, keys: effectiveKeys };
}

function makeBarLabelsLayer(params: {
  labelMode: 'none' | 'values' | 'totals' | 'both';
  keys: string[];
  layout: 'vertical' | 'horizontal';
  groupMode: 'stacked' | 'grouped';
  labelSkipWidth: number;
  labelSkipHeight: number;
  totalsMode: 'auto' | 'top' | 'right';
  totalsOffset: number;
  valueFormatFn?: ((v: number) => string) | string;
}) {
  const {
    labelMode,
    keys,
    layout,
    labelSkipWidth,
    labelSkipHeight,
    totalsMode,
    totalsOffset,
    valueFormatFn,
  } = params;

  const showValues = labelMode === 'values' || labelMode === 'both';
  const showTotals = labelMode === 'totals' || labelMode === 'both';

  const wantTopTotals =
    showTotals && (totalsMode === 'top' || (totalsMode !== 'right' && layout === 'vertical'));
  const wantRightTotals =
    showTotals && (totalsMode === 'right' || (totalsMode !== 'top' && layout === 'horizontal'));

  const format = (v: number) => {
    if (typeof valueFormatFn === 'function') return valueFormatFn(v);
    return String(v);
  };

  return function BarLabelsLayer(layerProps: any) {
    const bars = Array.isArray(layerProps?.bars) ? layerProps.bars : [];
    if (bars.length === 0) return null;

    const font = `${LABEL_FONT_SIZE_PX}px ${LABEL_FONT_FAMILY}`;
    const totalAnchorByIndex = new Map<string, any>();
    const rowByIndex = new Map<string, any>();

    for (const bar of bars) {
      const indexValue = String(
        bar?.data?.indexValue ?? bar?.data?.data?.label ?? bar?.data?.label ?? ''
      );
      if (!indexValue) continue;

      const row = bar?.data?.data ?? bar?.data ?? {};
      if (!rowByIndex.has(indexValue)) rowByIndex.set(indexValue, row);

      const prev = totalAnchorByIndex.get(indexValue);
      if (!prev) {
        totalAnchorByIndex.set(indexValue, bar);
      } else {
        if (layout === 'vertical') {
          if (bar.y < prev.y) totalAnchorByIndex.set(indexValue, bar);
        } else {
          if (bar.x + bar.width > prev.x + prev.width) totalAnchorByIndex.set(indexValue, bar);
        }
      }
    }

    const texts: any[] = [];

    if (showValues) {
      for (const bar of bars) {
        const w = Number(bar?.width ?? 0);
        const h = Number(bar?.height ?? 0);
        if (w <= 0 || h <= 0) continue;
        if (w < labelSkipWidth) continue;
        if (h < labelSkipHeight) continue;

        const v = Number(bar?.data?.value);
        if (!Number.isFinite(v)) continue;

        const text = format(v);
        const textWidth = measureChartTextWidth(text, font, 7);

        const x = bar.x + w / 2;
        const y = bar.y + h / 2 + LABEL_FONT_SIZE_PX / 2 - 2;
        if (textWidth + 4 > w) continue;

        texts.push(
          <text
            key={`seg-${bar.key}`}
            x={x}
            y={y}
            textAnchor="middle"
            style={{
              fontFamily: LABEL_FONT_FAMILY,
              fontSize: LABEL_FONT_SIZE_PX,
              fill: '#111',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            {text}
          </text>
        );
      }
    }

    if (showTotals) {
      totalAnchorByIndex.forEach((bar, indexValue) => {
        const row = rowByIndex.get(indexValue) ?? {};
        const total = sumRow(keys, row);
        const text = format(total);

        let x = 0;
        let y = 0;

        if (wantTopTotals) {
          x = bar.x + bar.width / 2;
          y = bar.y - totalsOffset;
        } else if (wantRightTotals) {
          x = bar.x + bar.width + totalsOffset;
          y = bar.y + bar.height / 2 + LABEL_FONT_SIZE_PX / 2 - 2;
        } else {
          x = bar.x + bar.width / 2;
          y = bar.y - totalsOffset;
        }

        texts.push(
          <text
            key={`tot-${indexValue}`}
            x={x}
            y={y}
            textAnchor={wantRightTotals ? 'start' : 'middle'}
            style={{
              fontFamily: LABEL_FONT_FAMILY,
              fontSize: LABEL_FONT_SIZE_PX,
              fontWeight: 600,
              fill: '#111',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            {text}
          </text>
        );
      });
    }

    if (texts.length === 0) return null;
    return <g>{texts}</g>;
  };
}

const BarChartTile = forwardRef<any, BarChartTileProps>(
  (
    {
      data,
      tileDef,
      layoutHeight,
      layoutWidth,
      direction = 'vertical',
      showLegendOverride,
      setError,
      initialized = false,
      displayContext = 'dashboard',
      ...rest
    },
    ref
  ) => {
    const chartRef = useRef<any>(null);

    useImperativeHandle(ref, () => ({
      download: (...args: any[]) => chartRef.current?.download?.(...args),
      getPopoverRef: (...args: any[]) => chartRef.current?.getPopoverRef?.(...args),
    }));

    const s = useMemo(() => tileDef?.settings ?? {}, [tileDef?.settings]);
    const useCategorical = (s as any).useCategorical !== false;

    const popoverTooltipPlacement = (s as any)?.tooltipPlacement;
    const popoverTooltipDistance = (s as any)?.tooltipDistance;
    const popoverTooltipSkidding = (s as any)?.tooltipSkidding;
    const popoverOptions = useMemo(
      () => ({
        hoist: true,
        variant: 'chart',
        placement: (s as any)?.tooltipPlacement ?? 'top',
        distance:
          typeof (s as any)?.tooltipDistance === 'number' ? (s as any).tooltipDistance : 8,
        skidding:
          typeof (s as any)?.tooltipSkidding === 'number' ? (s as any).tooltipSkidding : 0,
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [popoverTooltipPlacement, popoverTooltipDistance, popoverTooltipSkidding]
    );

    const padTop = Number(s?.tilePaddingTop ?? 0);
    const padRight = Number(s?.tilePaddingRight ?? 0);
    const padBottom = Number(s?.tilePaddingBottom ?? 0);
    const padLeft = Number(s?.tilePaddingLeft ?? 0);

    const layoutOrientation: 'vertical' | 'horizontal' =
      s?.orientation === 'horizontal' || s?.layout === 'horizontal'
        ? 'horizontal'
        : s?.orientation === 'vertical' || s?.layout === 'vertical'
          ? 'vertical'
          : direction;

    const legendTooltip = useMemo(() => {
      return (node: any) => String(node?.label ?? node?.id ?? '');
    }, []);

    const { formattedData, keys: effectiveKeys } = useMemo(
      () => buildBarChartData(data, tileDef),
      [data, tileDef]
    );

    const keysForChart = effectiveKeys;
    const hasSeries = keysForChart.length > 0;
    const hasRows = formattedData.length > 0;

    useEffect(() => {
      if (!setError) return;
      if (!initialized) return;
      setError(null);
    }, [setError, initialized, hasSeries, hasRows]);

    const showLegend =
      showLegendOverride !== undefined
        ? showLegendOverride
        : tileDef?.show_legend === true || s?.show_legend === true || s?.showLegend === true;

    const legendAnchor = showLegend ? s?.legendAnchor || 'bottom' : undefined;
    const legendDirection =
      legendAnchor === 'left' ? s?.legendDirection || 'column' : s?.legendDirection || 'row';

    const sizerRef = useRef<HTMLDivElement>(null);
    const [innerW, setInnerW] = useState(0);
    const [innerH, setInnerH] = useState(0);

    useEffect(() => {
      if (!sizerRef.current || typeof ResizeObserver === 'undefined') return;
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const w = Math.floor(entry.contentRect.width);
          const h = Math.floor(entry.contentRect.height);
          if (w > 0) setInnerW((prev) => (prev !== w ? w : prev));
          if (h > 0) setInnerH((prev) => (prev !== h ? h : prev));
        }
      });
      ro.observe(sizerRef.current);
      return () => ro.disconnect();
    }, []);

    const longestLabelPx = useMemo(() => {
      return formattedData.reduce((max, d) => {
        const w = d.label
          ? measureChartTextWidth(String(d.label), `14px ${LABEL_FONT_FAMILY}`, 7)
          : 0;
        return w > max ? w : max;
      }, 0);
    }, [formattedData]);

    const axisTickSize =
      typeof s?.axisTickSize === 'number' ? s.axisTickSize : DEFAULT_AXIS_TICK_SIZE;
    const axisTickPadding =
      typeof s?.axisTickPadding === 'number' ? s.axisTickPadding : DEFAULT_AXIS_TICK_PADDING;

    const legendItemWidth = typeof s?.legendItemWidth === 'number' ? s.legendItemWidth : 100;
    const legendItemHeight = typeof s?.legendItemHeight === 'number' ? s.legendItemHeight : 20;
    const legendTranslateX = typeof s?.legendTranslateX === 'number' ? s.legendTranslateX : 0;
    const legendTranslateY =
      typeof s?.legendTranslateY === 'number'
        ? s.legendTranslateY
        : legendAnchor === 'bottom'
          ? 50
          : 0;

    const effectiveW = innerW > 0 ? innerW : Math.max(1, layoutWidth - (padLeft + padRight));
    const effectiveH = innerH > 0 ? innerH : Math.max(1, layoutHeight - (padTop + padBottom));

    const maxLeftMarginRatio =
      typeof s?.maxLeftMarginRatio === 'number'
        ? Math.min(Math.max(s.maxLeftMarginRatio, 0.1), 0.8)
        : DEFAULT_MAX_LEFT_MARGIN_RATIO;

    const maxLeftMargin = Math.floor(effectiveW * maxLeftMarginRatio);

    const baseLeftMarginForHorizontal = Math.ceil(longestLabelPx + axisTickPadding + 12);
    const legendLeftFootprint =
      legendAnchor === 'left' ? Math.ceil(legendItemWidth + legendTranslateX + 12) : 0;
    const legendBottomFootprint =
      legendAnchor === 'bottom' ? Math.ceil(legendItemHeight + legendTranslateY + 12) : 0;

    const layoutStrategy: 'auto' | 'manual' = s?.layoutStrategy === 'manual' ? 'manual' : 'auto';

    const defaultTop = typeof s?.marginTop === 'number' ? s.marginTop : 24;
    const defaultRight = typeof s?.marginRight === 'number' ? s.marginRight : 16;
    const defaultBottom =
      typeof s?.marginBottom === 'number'
        ? s.marginBottom
        : layoutOrientation === 'vertical'
          ? 48
          : 72;
    const defaultLeft = typeof s?.marginLeft === 'number' ? s.marginLeft : 60;

    let marginLeftAuto = defaultLeft;
    if (layoutOrientation === 'horizontal') {
      marginLeftAuto = Math.max(defaultLeft, baseLeftMarginForHorizontal);
    }
    marginLeftAuto = Math.min(Math.max(marginLeftAuto, legendLeftFootprint), maxLeftMargin);

    const rawBottomLegendOffset =
      typeof s?.axisBottomLegendOffset === 'number'
        ? s.axisBottomLegendOffset
        : DEFAULT_BOTTOM_LEGEND_OFFSET;
    const absBottomLegendOffset = Math.abs(rawBottomLegendOffset);

    let marginBottomAuto = defaultBottom;
    if (layoutOrientation === 'vertical') {
      const bottomLabelHeightEstimate = LABEL_FONT_SIZE_PX + axisTickPadding + 12;
      const axisLegendRoom = absBottomLegendOffset + 18;
      marginBottomAuto = Math.max(defaultBottom, bottomLabelHeightEstimate, axisLegendRoom);
    }
    marginBottomAuto = Math.max(marginBottomAuto, legendBottomFootprint);

    const maxTotalVerticalMargin = Math.floor(effectiveH * 0.35);
    const maxSingleVerticalMargin = Math.floor(effectiveH * 0.28);

    let marginTopAuto = Math.min(defaultTop, maxSingleVerticalMargin);
    marginBottomAuto = Math.min(marginBottomAuto, maxSingleVerticalMargin);

    if (marginTopAuto + marginBottomAuto > maxTotalVerticalMargin) {
      const excess = marginTopAuto + marginBottomAuto - maxTotalVerticalMargin;
      const reduceTopFirst = Math.min(excess, Math.max(0, marginTopAuto - 8));
      marginTopAuto -= reduceTopFirst;
      const remainingExcess = excess - reduceTopFirst;
      if (remainingExcess > 0) {
        const minBottom = Math.max(24, absBottomLegendOffset + 14);
        marginBottomAuto = Math.max(minBottom, marginBottomAuto - remainingExcess);
      }
    }

    const computedMargin = useMemo(() => {
      const manualMargin = getContextualChartMargins(
        s,
        displayContext,
        showLegend,
        {
          top: 24,
          right: 16,
          bottom: layoutOrientation === 'vertical' ? 48 : 72,
          left: 60,
        }
      );

      if (layoutStrategy === 'manual' && manualMargin) return manualMargin;

      return {
        top: marginTopAuto,
        bottom: marginBottomAuto,
        right: defaultRight,
        left: Math.min(marginLeftAuto, maxLeftMargin),
      };
    }, [
      s,
      displayContext,
      showLegend,
      layoutStrategy,
      layoutOrientation,
      marginTopAuto,
      marginBottomAuto,
      defaultRight,
      marginLeftAuto,
      maxLeftMargin,
    ]);

    const axisBottomLegend =
      s?.axisBottomLegend?.trim() ||
      (layoutOrientation === 'vertical'
        ? s?.customLabel || s?.labelField || 'Label'
        : s?.valueField || 'Value');

    const axisLeftLegend =
      s?.axisLeftLegend?.trim() ||
      (layoutOrientation === 'horizontal'
        ? s?.customLabel || s?.labelField || 'Label'
        : s?.valueField || 'Value');

    const axisBottomRotation =
      typeof s?.axisBottomRotation === 'number' ? s.axisBottomRotation : 0;
    const axisLeftRotation = typeof s?.axisLeftRotation === 'number' ? s.axisLeftRotation : 0;

    const showBottomLabels =
      s?.showBottomLabels !== undefined ? !!s.showBottomLabels : effectiveH > 140;

    const valueFormatFn = useMemo(
      () =>
        makeSmartValueFormatter({
          valueFormat: s?.valueFormat,
          currency: s?.currency,
          numberNotation: (s as any)?.numberNotation,
          compactThreshold: (s as any)?.compactThreshold,
          compactDecimals: (s as any)?.compactDecimals,
          prefixCurrencyOnCompact: (s as any)?.prefixCurrencyOnCompact,
        }),
      [s]
    );

    const axisBottom = useMemo(() => {
      if (!showBottomLabels) return null;
      return {
        tickSize: axisTickSize,
        tickPadding: axisTickPadding,
        tickRotation: axisBottomRotation,
        legend: axisBottomLegend,
        legendPosition: 'middle',
        legendOffset: rawBottomLegendOffset,
      };
    }, [
      showBottomLabels,
      axisTickSize,
      axisTickPadding,
      axisBottomRotation,
      axisBottomLegend,
      rawBottomLegendOffset,
    ]);

    const axisLeft = useMemo(
      () => ({
        tickSize: axisTickSize,
        tickPadding: axisTickPadding,
        tickRotation: axisLeftRotation,
        legend: axisLeftLegend,
        legendPosition: 'middle',
        legendOffset:
          typeof s?.axisLeftLegendOffset === 'number'
            ? s.axisLeftLegendOffset
            : DEFAULT_LEFT_LEGEND_OFFSET,
        format: valueFormatFn,
      }),
      [
        axisTickSize,
        axisTickPadding,
        axisLeftRotation,
        axisLeftLegend,
        s?.axisLeftLegendOffset,
        valueFormatFn,
      ]
    );

    const legends = useMemo(() => {
      if (!showLegend) return [];
      return [
        {
          dataFrom: 'keys',
          anchor: legendAnchor,
          direction: legendDirection,
          justify: false,
          translateX: legendTranslateX,
          translateY: legendTranslateY,
          itemsSpacing: typeof s?.legendItemsSpacing === 'number' ? s.legendItemsSpacing : 8,
          itemWidth: Math.max(60, legendItemWidth),
          itemHeight: legendItemHeight,
          itemDirection:
            s?.legendItemDirection ||
            (legendAnchor === 'left' ? 'left-to-right' : 'top-to-bottom'),
          itemOpacity: typeof s?.legendItemOpacity === 'number' ? s.legendItemOpacity : 0.85,
          symbolSize: typeof s?.legendSymbolSize === 'number' ? s.legendSymbolSize : 16,
          effects: [{ on: 'hover', style: { itemOpacity: 1 } }],
        },
      ];
    }, [
      showLegend,
      legendAnchor,
      legendDirection,
      legendTranslateX,
      legendTranslateY,
      legendItemWidth,
      legendItemHeight,
      s?.legendItemsSpacing,
      s?.legendItemDirection,
      s?.legendItemOpacity,
      s?.legendSymbolSize,
    ]);

    const enableGridX = !!s?.enableGridX;
    const enableGridY = s?.enableGridY !== false;
    const gridXValues = Array.isArray((s as any).gridXValues)
      ? (s as any).gridXValues
      : undefined;
    const gridYValues = Array.isArray((s as any).gridYValues)
      ? (s as any).gridYValues
      : undefined;

    const groupMode: 'stacked' | 'grouped' =
      s?.groupMode === 'grouped' || s?.groupMode === 'stacked'
        ? s.groupMode
        : DEFAULT_GROUP_MODE;

    const innerPadding = typeof s?.innerPadding === 'number' ? s.innerPadding : undefined;
    const barPadding = typeof s?.padding === 'number' ? s.padding : undefined;
    const borderRadius = typeof s?.borderRadius === 'number' ? s.borderRadius : undefined;
    const borderWidth = typeof s?.borderWidth === 'number' ? s.borderWidth : undefined;
    const borderColor = normalizeNivoBorderColor(s?.borderColor);

    const tooltipRenderer = useMemo(() => {
      return buildHarborItemTooltip(
        s,
        {
          getSeries: (e: any) => e?.id ?? e?.key ?? e?.data?.id ?? e?.data?.key ?? '',
          getLabel: (e: any) => e?.data?.indexValue ?? e?.data?.label ?? e?.indexValue ?? '',
          getValue: (e: any) => {
            const raw = e?.data?.value ?? e?.value ?? '';
            const asNum = Number(raw);
            return Number.isFinite(asNum) ? valueFormatFn(asNum) : raw;
          },
        },
        { dotMode: 'before' }
      );
    }, [s, valueFormatFn]);

    type BarLabelMode = 'none' | 'values' | 'totals' | 'both';
    const labelMode: BarLabelMode = ((s as any).barLabelMode ?? 'none') as BarLabelMode;

    const userSkipW = typeof s?.labelSkipWidth === 'number' ? s.labelSkipWidth : 0;
    const userSkipH = typeof s?.labelSkipHeight === 'number' ? s.labelSkipHeight : 30;
    const totalsMode = ((s as any).totalsMode ?? 'auto') as 'auto' | 'top' | 'right';
    const totalsOffset = typeof s?.totalsOffset === 'number' ? s.totalsOffset : 10;

    const labelsLayer = useMemo(
      () =>
        makeBarLabelsLayer({
          labelMode,
          keys: keysForChart,
          layout: layoutOrientation,
          groupMode,
          labelSkipWidth: userSkipW,
          labelSkipHeight: userSkipH,
          totalsMode,
          totalsOffset,
          valueFormatFn,
        }),
      [
        labelMode,
        keysForChart,
        layoutOrientation,
        groupMode,
        userSkipW,
        userSkipH,
        totalsMode,
        totalsOffset,
        valueFormatFn,
      ]
    );

    const config: any = useMemo(() => {
      const w = innerW > 0 ? innerW : Math.max(1, layoutWidth - (padLeft + padRight));
      const h = innerH > 0 ? innerH : Math.max(1, layoutHeight - (padTop + padBottom));
      if (w <= 0 || h <= 0) return null;

      const axisForLayout =
        layoutOrientation === 'vertical'
          ? { axisBottom, axisLeft }
          : {
            axisBottom: { ...axisBottom, format: valueFormatFn },
            axisLeft,
          };

      return {
        data: formattedData,
        keys: keysForChart,
        indexBy: 'label',
        layout: layoutOrientation,
        groupMode,

        minValue: 0,
        maxValue: 'auto',

        enableLabel: false,
        labelSkipWidth: 999999,
        labelSkipHeight: 999999,

        margin: computedMargin,
        ...axisForLayout,

        enableGridX,
        enableGridY,
        ...(gridXValues ? { gridXValues } : {}),
        ...(gridYValues ? { gridYValues } : {}),

        ...(typeof innerPadding === 'number' ? { innerPadding } : {}),
        ...(typeof barPadding === 'number' ? { padding: barPadding } : {}),
        ...(typeof borderRadius === 'number' ? { borderRadius } : {}),
        ...(typeof borderWidth === 'number' ? { borderWidth } : {}),
        ...(borderColor ? { borderColor } : {}),

        ...(showLegend ? { legends, legendTooltip } : {}),

        isInteractive: s?.isInteractive !== false,
        animate: s?.animate !== false,
        motionConfig: s?.motionConfig ?? 'default',
        isFocusable: !!s?.isFocusable,

        itemTooltip: tooltipRenderer,
        layers: showLegend
          ? ['grid', 'axes', 'bars', 'markers', 'legends', labelsLayer]
          : ['grid', 'axes', 'bars', 'markers', labelsLayer],

        height: Math.max(1, h),
        width: Math.max(1, w),
      };
    }, [
      innerW,
      layoutWidth,
      padLeft,
      padRight,
      innerH,
      layoutHeight,
      padTop,
      padBottom,
      formattedData,
      keysForChart,
      layoutOrientation,
      groupMode,
      computedMargin,
      axisBottom,
      axisLeft,
      valueFormatFn,
      enableGridX,
      enableGridY,
      gridXValues,
      gridYValues,
      innerPadding,
      barPadding,
      borderRadius,
      borderWidth,
      borderColor,
      showLegend,
      legends,
      legendTooltip,
      s?.isInteractive,
      s?.animate,
      s?.motionConfig,
      s?.isFocusable,
      tooltipRenderer,
      labelsLayer,
    ]);

    const modifyHbrConfig = useCallback(
      (finalCfg: any, _createElement: any, util: any) => {
        finalCfg = applyCommonHarborRuntimeInModifyConfig(
          finalCfg,
          {
            grid: { enableGridX, enableGridY, gridXValues, gridYValues },
            axes: {
              xAxisLabel: (s as any).xAxisLabel,
              yAxisLabel: (s as any).yAxisLabel,
              axisBottomLegend: (s as any).axisBottomLegend,
              axisLeftLegend: (s as any).axisLeftLegend,
              axisBottomTickValues: (s as any).axisBottomTickValues,
              axisLeftTickValues: (s as any).axisLeftTickValues,
            },
          },
          util,
          {
            kind: 'bar',
            keys: keysForChart,
            defaultColorBy: 'id',
            postMergeUtilFnName: 'postUpdateMergedBarConfig',
          }
        );

        finalCfg.enableLabel = false;
        finalCfg.labelSkipWidth = 999999;
        finalCfg.labelSkipHeight = 999999;

        finalCfg.minValue = 0;
        finalCfg.maxValue = 'auto';

        if (layoutOrientation === 'vertical') {
          finalCfg.axisLeft = { ...(finalCfg.axisLeft || {}), format: valueFormatFn };
        } else {
          finalCfg.axisBottom = { ...(finalCfg.axisBottom || {}), format: valueFormatFn };
        }

        finalCfg.layers = showLegend
          ? ['grid', 'axes', 'bars', 'markers', 'legends', labelsLayer]
          : ['grid', 'axes', 'bars', 'markers', labelsLayer];

        if (!showLegend) {
          finalCfg.legends = [];
          delete finalCfg.legendTooltip;
        } else {
          const existingLegends = Array.isArray(finalCfg.legends) ? finalCfg.legends : [];

          finalCfg.legends =
            existingLegends.length > 0
              ? existingLegends.map((legend: any) => ({
                ...legend,
                anchor: legendAnchor,
                direction: legendDirection,
                translateX: legendTranslateX,
                translateY: legendTranslateY,
                itemsSpacing:
                  typeof s?.legendItemsSpacing === 'number'
                    ? s.legendItemsSpacing
                    : legend.itemsSpacing ?? 8,
                itemWidth: Math.max(60, legendItemWidth),
                itemHeight: legendItemHeight,
                itemOpacity:
                  typeof s?.legendItemOpacity === 'number'
                    ? s.legendItemOpacity
                    : legend.itemOpacity ?? 0.85,
                symbolSize:
                  typeof s?.legendSymbolSize === 'number'
                    ? s.legendSymbolSize
                    : legend.symbolSize ?? 16,
              }))
              : legends;

          finalCfg.legendTooltip = legendTooltip;
        }

        finalCfg.popoverOptions = {
          ...(finalCfg.popoverOptions || {}),
          hoist: true,
          variant: 'chart',
          placement: (s as any)?.tooltipPlacement ?? 'top',
          distance:
            typeof (s as any)?.tooltipDistance === 'number' ? (s as any).tooltipDistance : 8,
          skidding:
            typeof (s as any)?.tooltipSkidding === 'number' ? (s as any).tooltipSkidding : 0,
        };

        finalCfg.tooltipZIndex = 999999;

        return finalCfg;
      },
      [
        enableGridX,
        enableGridY,
        gridXValues,
        gridYValues,
        s,
        keysForChart,
        layoutOrientation,
        valueFormatFn,
        labelsLayer,
        legendTooltip,
        legends,
        showLegend,
        legendAnchor,
        legendDirection,
        legendTranslateX,
        legendTranslateY,
        legendItemWidth,
        legendItemHeight,
      ]
    );

    const chartKeyColorScheme = (s as any).colorScheme;
    const chartKeyColors = (s as any).colors;
    const chartKeySeriesColors = (s as any).seriesColors;
    const chartKeyTooltipPlacement = (s as any).tooltipPlacement;
    const chartKeyTooltipDistance = (s as any).tooltipDistance;
    const chartKeyTooltipSkidding = (s as any).tooltipSkidding;
    const chartKeyTooltipMode = (s as any).tooltipMode;
    const chartRenderKey = useMemo(() => {
      return stableStringify({
        layoutOrientation,
        groupMode,
        keysForChart,
        formattedData,
        innerW,
        innerH,
        useCategorical,
        showLegend,
        legendAnchor,
        legendDirection,
        legendItemWidth,
        legendItemHeight,
        legendTranslateX,
        legendTranslateY,
        colorScheme: (s as any).colorScheme,
        colors: (s as any).colors,
        seriesColors: (s as any).seriesColors,
        tooltipPlacement: (s as any).tooltipPlacement,
        tooltipDistance: (s as any).tooltipDistance,
        tooltipSkidding: (s as any).tooltipSkidding,
        tooltipMode: (s as any).tooltipMode,
        motionConfig: s?.motionConfig,
        animate: s?.animate,
        borderColor,
        borderWidth,
        borderRadius,
        barPadding,
        innerPadding,
        displayContext,
        computedMargin,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      layoutOrientation,
      groupMode,
      keysForChart,
      formattedData,
      innerW,
      innerH,
      useCategorical,
      showLegend,
      legendAnchor,
      legendDirection,
      legendItemWidth,
      legendItemHeight,
      legendTranslateX,
      legendTranslateY,
      chartKeyColorScheme,
      chartKeyColors,
      chartKeySeriesColors,
      chartKeyTooltipPlacement,
      chartKeyTooltipDistance,
      chartKeyTooltipSkidding,
      chartKeyTooltipMode,
      s?.motionConfig,
      s?.animate,
      borderColor,
      borderWidth,
      borderRadius,
      barPadding,
      innerPadding,
      displayContext,
      computedMargin,
    ]);

    useEffect(() => {
      let cancelled = false;

      const elevatePopover = async () => {
        try {
          const popoverEl = await chartRef.current?.getPopoverRef?.();
          if (!popoverEl || cancelled) return;

          popoverEl.style.zIndex = '999999';
          if (!popoverEl.style.position) {
            popoverEl.style.position = 'relative';
          }
        } catch {
          // no-op
        }
      };

      elevatePopover();

      return () => {
        cancelled = true;
      };
    }, [chartRenderKey]);

    if (!initialized) {
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            minHeight: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <HbrSpinner>Loading...</HbrSpinner>
        </div>
      );
    }

    return (
      <div
        style={{
          height: '100%',
          width: '100%',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflow: 'visible',
          boxSizing: 'border-box',
        }}
      >
        <div
          ref={sizerRef}
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'stretch',
            overflow: 'visible',
          }}
        >
          {config ? (
            <HbrBarChart
              key={chartRenderKey}
              categorical={useCategorical}
              config={config}
              modifyHbrConfig={modifyHbrConfig}
              popoverOptions={popoverOptions as any}
              style={{ height: '100%', width: '100%', overflow: 'visible' }}
              ref={chartRef}
              {...rest}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <HbrSpinner>Loading...</HbrSpinner>
            </div>
          )}
        </div>
      </div>
    );
  }
);

BarChartTile.displayName = 'BarChartTile';
export default BarChartTile;